import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Box, Typography, Paper } from '@mui/material';
import { Image as ImageDropIcon } from '@mui/icons-material';
import SortableComponent from './SortableComponent';
import ComponentPreview from './ComponentPreview';
import ContainerCanvas from './ContainerCanvas';
import DropZone from './DropZone';
import { useImageFileDrop } from '../../hooks/useImageFileDrop';

const CONTAINER_TYPES = new Set(['If', 'Switch']);

const Canvas = ({
  screen,
  selectedComponent,
  hoveredComponent,
  onSelectComponent,
  onDeleteComponent,
  onDuplicateComponent,
  addComponent,
  onDropError,
  addComponentToBranch,
  reorderBranchComponents,
  removeComponentFromBranch,
  validationMap,
}) => {
  const { setNodeRef } = useDroppable({
    id: 'canvas',
    data: {
      type: 'canvas',
    },
  });

  // Native OS file drop (separate from @dnd-kit) -> creates Image components.
  const { isDraggingFile, dropHandlers } = useImageFileDrop({
    screen,
    addComponent,
    onError: onDropError,
  });

  const handleComponentClick = (component) => {
    onSelectComponent(component);
  };

  const handleDeleteComponent = (componentId) => {
    onDeleteComponent(componentId);
  };

  if (!screen) {
    return (
      <Box className="canvas-placeholder">
        <Typography variant="h6" color="text.secondary">
          No screen selected
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={setNodeRef}
      className="canvas"
      data-testid="canvas"
      sx={{ position: 'relative' }}
      {...dropHandlers}
    >
      {isDraggingFile && (
        <Box className="canvas-image-drop-overlay" data-testid="canvas-image-drop-overlay">
          <ImageDropIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Drop image to add it here
          </Typography>
          <Typography variant="caption">JPEG or PNG, up to 300KB</Typography>
        </Box>
      )}
      <Paper className="canvas-screen" elevation={0}>
        <Box className="canvas-screen__header">
          <Typography variant="h6" component="h2">
            {screen.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Components: {screen.components.length}/50
          </Typography>
        </Box>

        <Box className="canvas-screen__content">
          {screen.components.length === 0 ? (
            <DropZone index={0} text="Drop components here to build your flow" />
          ) : (
            <>
              <DropZone 
                index={0} 
                isTargetForHoveredComponent={false}
              />
              <SortableContext
                items={screen.components.map(comp => comp.id)}
                strategy={verticalListSortingStrategy}
              >
                {screen.components.map((component, index) => (
                  <React.Fragment key={component.id}>
                    {CONTAINER_TYPES.has(component.type) ? (
                      <ContainerCanvas
                        component={component}
                        isSelected={selectedComponent?.id === component.id}
                        onSelect={() => handleComponentClick(component)}
                        onDelete={() => handleDeleteComponent(component.id)}
                        onSelectNested={onSelectComponent}
                        selectedComponent={selectedComponent}
                        addComponentToBranch={addComponentToBranch}
                        reorderBranchComponents={reorderBranchComponents}
                        removeComponentFromBranch={removeComponentFromBranch}
                      />
                    ) : (
                      <SortableComponent
                        id={component.id}
                        component={component}
                        isSelected={selectedComponent?.id === component.id}
                        isHovered={hoveredComponent === component.id}
                        isHidden={component.properties?.visible === false}
                        hasCondition={!!component.properties?._condition}
                        validation={validationMap?.byComponent?.get(component.id)}
                        onClick={() => handleComponentClick(component)}
                        onDelete={() => handleDeleteComponent(component.id)}
                        onDuplicate={onDuplicateComponent ? () => onDuplicateComponent(component.id) : undefined}
                        isDraggable={component.type !== 'Footer'}
                        isDeletable={component.type !== 'Footer'}
                      >
                        <ComponentPreview component={component} />
                      </SortableComponent>
                    )}
                    <DropZone
                      index={index + 1}
                      isTargetForHoveredComponent={hoveredComponent === component.id}
                    />
                  </React.Fragment>
                ))}
              </SortableContext>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Canvas;